import { useSelectedDealer, useSelectedLocation } from "components/DealersAndLocationsDropdown/hooks";
import { typedQueryClient, usePermanentRealTimeUser, useTypedQuery } from "hooks";
import { DEALER_DOCUMENTATION_CATEGORIES, ROLES } from "models";
import { DealerDocumentationsQueryKeys } from "modules/DealerDocumentations/queryKeys";
import { DEALER_DOCUMENTATIONS_STORAGE_KEYS } from "modules/DealerDocumentations/storageKeys";
import { getPreference, setPreference } from "utils/common";

import type { DealerDocumentationsFilters } from "modules/DealerDocumentations/queryKeys";
import type { AtLeastOne } from "utils/type";

const useDefaultDealerDocumentationsFilters = () => {
  const { user } = usePermanentRealTimeUser();
  const { selectedDealer } = useSelectedDealer();
  const { selectedLocation } = useSelectedLocation();
  const isMechanicRole = user?.role_id === ROLES.Mechanic;

  return {
    category_id: DEALER_DOCUMENTATION_CATEGORIES.Manual,
    dealer_ids: !isMechanicRole && selectedDealer ? [selectedDealer.id] : [],
    dealer_location_ids: isMechanicRole && selectedLocation ? [selectedLocation.id] : [],
    visible_to_mechanic: true
  };
};

const persistDealerDocumentationsFilters = (filters: DealerDocumentationsFilters) => {
  setPreference(DEALER_DOCUMENTATIONS_STORAGE_KEYS.CategoryPreference, filters.category_id);
  setPreference(DEALER_DOCUMENTATIONS_STORAGE_KEYS.DealerPreference, filters.dealer_ids);
  setPreference(DEALER_DOCUMENTATIONS_STORAGE_KEYS.LocationPreference, filters.dealer_location_ids);
  setPreference(DEALER_DOCUMENTATIONS_STORAGE_KEYS.VisibleToMechanicPreference, filters.visible_to_mechanic);
};

export const setDealerDocumentationsFilters = (filters: AtLeastOne<DealerDocumentationsFilters>) => {
  const previousFilters = typedQueryClient.getQueryData(DealerDocumentationsQueryKeys.filters);
  if (!previousFilters) return;

  const newFilters = { ...previousFilters, ...filters };
  persistDealerDocumentationsFilters(newFilters);

  typedQueryClient.setQueryData(DealerDocumentationsQueryKeys.filters, newFilters);
};

export const useDealerDocumentationsFilters = () => {
  const defaultFilters = useDefaultDealerDocumentationsFilters();

  return useTypedQuery({
    queryKey: DealerDocumentationsQueryKeys.filters,
    initialData: {
      category_id: getPreference(DEALER_DOCUMENTATIONS_STORAGE_KEYS.CategoryPreference, defaultFilters.category_id),
      dealer_ids: getPreference(DEALER_DOCUMENTATIONS_STORAGE_KEYS.DealerPreference, defaultFilters.dealer_ids),
      dealer_location_ids: getPreference(DEALER_DOCUMENTATIONS_STORAGE_KEYS.LocationPreference, defaultFilters.dealer_location_ids),
      visible_to_mechanic: getPreference(DEALER_DOCUMENTATIONS_STORAGE_KEYS.VisibleToMechanicPreference, defaultFilters.visible_to_mechanic)
    }
  }).data;
};
